import { useEffect, useState } from 'react';
import {
    Alert,
    AlertDescription,
    AlertIcon,
    AlertTitle,
    Badge,
    Box,
    Button,
    Divider,
    FormControl,
    FormHelperText,
    FormLabel,
    HStack,
    IconButton,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Spinner,
    Text,
    useToast,
    VStack,
} from '@chakra-ui/react';
import PropTypes from 'prop-types';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import { authAPI } from '../services/api';
import { formatDateTimeSeconds } from '../utils/dateUtils';

const MAX_FOBS = 3;

const StudentScanCodeModal = ({ isOpen, onClose, student }) => {
    const toast = useToast();
    const [fobs, setFobs] = useState([]);
    const [newCode, setNewCode] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isAdding, setIsAdding] = useState(false);
    const [removingId, setRemovingId] = useState(null);

    const loadFobs = async () => {
        if (!student?.id) return;
        setIsLoading(true);
        try {
            const res = await authAPI.frontdesk.listScanCodes(student.id);
            setFobs(res.data || []);
        } catch (error) {
            toast({
                title: '磁扣資料讀取失敗',
                description: error.response?.data?.detail || '請稍後再試。',
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen && student?.id) {
            setNewCode('');
            loadFobs();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, student?.id]);

    const handleAdd = async () => {
        const code = newCode.trim();
        if (!code || !student?.id) return;
        setIsAdding(true);
        try {
            await authAPI.frontdesk.addScanCode(student.id, code);
            setNewCode('');
            await loadFobs();
            toast({ title: '已新增磁扣', status: 'success', duration: 2500, isClosable: true });
        } catch (error) {
            toast({
                title: '新增磁扣失敗',
                description: error.response?.data?.detail || '請稍後再試。',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsAdding(false);
        }
    };

    const handleRemove = async (fob) => {
        setRemovingId(fob.id);
        try {
            await authAPI.frontdesk.removeScanCode(fob.id);
            await loadFobs();
            toast({ title: '已移除磁扣', status: 'success', duration: 2000, isClosable: true });
        } catch (error) {
            toast({
                title: '移除失敗',
                description: error.response?.data?.detail || '請稍後再試。',
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        } finally {
            setRemovingId(null);
        }
    };

    const atMax = fobs.length >= MAX_FOBS;

    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>磁扣管理</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <VStack align="stretch" spacing={4}>
                        <Box>
                            <Text fontWeight="bold" fontSize="lg">{student?.full_name || '--'}</Text>
                            <Text color="gray.600" fontSize="sm">
                                班級：{student?.class_name || student?.class?.name || '--'}
                            </Text>
                        </Box>

                        <Alert status="info" borderRadius="lg">
                            <AlertIcon />
                            <Box>
                                <AlertTitle fontSize="sm">操作說明</AlertTitle>
                                <AlertDescription fontSize="sm">
                                    把游標點在下方欄位，用讀卡機刷磁扣（號碼會自動填入）或手動輸入卡號，按「新增磁扣」。
                                    一位學生最多 {MAX_FOBS} 張。
                                </AlertDescription>
                            </Box>
                        </Alert>

                        <Box>
                            <HStack justify="space-between" mb={2}>
                                <Text fontWeight="bold">目前磁扣</Text>
                                <Badge colorScheme={atMax ? 'orange' : 'green'}>
                                    {fobs.length} / {MAX_FOBS}
                                </Badge>
                            </HStack>
                            {isLoading ? (
                                <Box py={6} textAlign="center"><Spinner /></Box>
                            ) : fobs.length === 0 ? (
                                <Text color="gray.500" fontSize="sm" py={2}>尚無磁扣，請於下方新增。</Text>
                            ) : (
                                <VStack align="stretch" spacing={2}>
                                    {fobs.map((fob) => (
                                        <HStack key={fob.id} justify="space-between" bg="gray.50" borderRadius="md" px={3} py={2}>
                                            <Box>
                                                <Text fontFamily="mono" fontSize="lg" letterSpacing="0.08em">{fob.code}</Text>
                                                <Text color="gray.500" fontSize="xs">新增：{formatDateTimeSeconds(fob.issued_at)}</Text>
                                            </Box>
                                            <IconButton
                                                aria-label="移除磁扣"
                                                icon={<FiTrash2 />}
                                                size="sm"
                                                colorScheme="red"
                                                variant="ghost"
                                                isLoading={removingId === fob.id}
                                                onClick={() => handleRemove(fob)}
                                            />
                                        </HStack>
                                    ))}
                                </VStack>
                            )}
                        </Box>

                        <Divider />

                        <FormControl>
                            <FormLabel>新增磁扣（卡號）</FormLabel>
                            <HStack>
                                <Input
                                    value={newCode}
                                    onChange={(e) => setNewCode(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleAdd();
                                        }
                                    }}
                                    placeholder="刷磁扣或手動輸入卡號"
                                    autoComplete="off"
                                    isDisabled={atMax}
                                />
                                <Button
                                    colorScheme="brand"
                                    leftIcon={<FiPlus />}
                                    onClick={handleAdd}
                                    isLoading={isAdding}
                                    isDisabled={atMax || !newCode.trim()}
                                    minW="110px"
                                >
                                    新增磁扣
                                </Button>
                            </HStack>
                            <FormHelperText>
                                {atMax
                                    ? `已達上限 ${MAX_FOBS} 張，請先移除再新增。`
                                    : '卡號會自動補零到 10 位；若已綁定其他學生會擋下並提示。'}
                            </FormHelperText>
                        </FormControl>
                    </VStack>
                </ModalBody>
                <ModalFooter>
                    <Button variant="ghost" onClick={onClose}>關閉</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

StudentScanCodeModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    student: PropTypes.shape({
        id: PropTypes.number,
        full_name: PropTypes.string,
        class_name: PropTypes.string,
        class: PropTypes.shape({
            name: PropTypes.string,
        }),
    }),
};

StudentScanCodeModal.defaultProps = {
    student: null,
};

export default StudentScanCodeModal;
